import { useState, useCallback, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

import { AddCircleOutlineOutlined as AddIcon } from "@mui/icons-material";
import PropTypes from "prop-types";
import Button from "../../../theme/components/Button.jsx";

import CostEntryForm from "./CostEntryForm.jsx";
import CostEntryRow from "./CostEntryRow.jsx";

/**
 * Main component for managing claim costs.
 * All changes are managed via value/onChange props (form state).
 * Changes are only persisted when the main form is saved.
 * File uploads are stored in form state and processed server-side on save.
 */
const ClaimCostsEditor = ({ disabled, value = [], onChange, onFormChange, onFormOpenChange }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingCost, setEditingCost] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tempIdCounter = useRef(0);

  // Generate a temporary ID for new costs
  const generateTempId = () => {
    tempIdCounter.current += 1;
    return `temp_${Date.now()}_${tempIdCounter.current}`;
  };

  // Calculate total from current costs
  const calculateTotal = useCallback((costList) => {
    return costList.reduce(
      (sum, cost) => sum + (parseFloat(cost.amount) || 0),
      0
    );
  }, []);

  const totalAmount = calculateTotal(value);

  const isFormOpen = showForm || !!editingCost;

  useEffect(() => {
    if (onFormOpenChange) {
      onFormOpenChange(isFormOpen);
    }
  }, [isFormOpen]);

  // Sync total to gesamtschadenkosten form field
  useEffect(() => {
    // Trigger onFormChange for gesamtschadenkosten field
    if (onFormChange) {
      onFormChange({
        target: { name: "gesamtschadenkosten", value: totalAmount },
      });
    }
  }, [totalAmount]);

  const handleAddClick = () => {
    setEditingCost(null);
    setShowForm(true);
  };

  const handleEditClick = (cost) => {
    setEditingCost(cost);
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingCost(null);
  };

  const handleSubmit = (formData) => {
    if (editingCost?.id) {
      // update existing cost
      const updatedCosts = value.map((cost) =>
        cost.id === editingCost.id ? { ...formData, id: editingCost.id } : cost
      );
      onChange(updatedCosts);
    } else {
      // add new cost with temporary ID
      const newCost = {
        ...formData,
        id: generateTempId(),
      };
      onChange([...value, newCost]);
    }

    setShowForm(false);
    setEditingCost(null);
  };

  const handleDeleteClick = (cost) => {
    setDeleteConfirm(cost);
  };

  const handleDeleteConfirm = () => {
    if (!deleteConfirm) {
      return;
    }

    const filteredCosts = value.filter((cost) => cost.id !== deleteConfirm.id);
    onChange(filteredCosts);
    setDeleteConfirm(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(amount || 0);
  };

  return (
    <Paper elevation={0} sx={{}}>
      {value.length === 0 && !showForm ? (
        <Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
          Noch keine Kosten erfasst
        </Typography>
      ) : (
        <Box>
          {value.map((cost) => (
            editingCost?.id === cost.id ? (
              <Paper
                variant="outlined"
                key={cost.id}
                sx={{ p: 2, mb: 1, borderColor: "primary.main", boxShadow: 3 }}
              >
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Kostenposition bearbeiten
                </Typography>
                <CostEntryForm
                  initialData={editingCost}
                  onSubmit={handleSubmit}
                  onCancel={handleCancelForm}
                />
              </Paper>
            ) : (
              <Box
                key={cost.id}
                sx={{
                  opacity: isFormOpen ? 0.3 : 1,
                  pointerEvents: isFormOpen ? "none" : "auto",
                  transition: "opacity 0.15s",
                }}
              >
                <CostEntryRow
                  cost={cost}
                  onEdit={handleEditClick}
                  onDelete={handleDeleteClick}
                  disabled={disabled || showForm}
                />
              </Box>
            )
          ))}
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mb: 2,
          mt: 4,
        }}
      >
        {!disabled && !showForm && !editingCost && (
          <Button
            startIcon={<AddIcon />}
            size="medium"
            onClick={handleAddClick}
          >
            Hinzufügen
          </Button>
        )}
      </Box>

      <Collapse in={showForm && !editingCost}>
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: "primary.main", boxShadow: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Neue Kostenposition
          </Typography>
          <CostEntryForm
            initialData={editingCost}
            onSubmit={handleSubmit}
            onCancel={handleCancelForm}
          />
        </Paper>
      </Collapse>

      {value.length > 0 && (
        <Box
          sx={{
            opacity: isFormOpen ? 0.3 : 1,
            pointerEvents: isFormOpen ? "none" : "auto",
            transition: "opacity 0.15s",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="subtitle1">Gesamtkosten</Typography>
            <Typography variant="h6" color="primary">
              {formatCurrency(totalAmount)}
            </Typography>
          </Box>
        </Box>
      )}

      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Kostenposition löschen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Soll diese Kostenposition wirklich gelöscht werden? Diese Aktion
            kann nicht rückgängig gemacht werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="tertiary" onClick={() => setDeleteConfirm(null)}>
            Abbrechen
          </Button>
          <Button onClick={handleDeleteConfirm}>Löschen</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

ClaimCostsEditor.propTypes = {
  disabled: PropTypes.bool,
  value: PropTypes.array,
  onChange: PropTypes.func,
  onFormChange: PropTypes.func,
  onFormOpenChange: PropTypes.func,
};

export default ClaimCostsEditor;
